import React from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import API from "../API";

const SUPPORTED_VEHICLE_TYPES = [0, 2, 4, 5, 7, 8, 9];
const VEHICLE_LABELS = { 0: "Motorsiklet", 2: "Doblo", 4: "Panelvan", 5: "Kamyonet", 7: "Kamyon (6 Teker)", 8: "Kamyon (8 Teker)", 9: "Kamyon (10 Teker)" };
const DOCUMENT_COLUMNS = [
  ["src", "SRC"], ["psychotechnical", "Psikoteknik"],
  ["authorization", "K Yetki"], ["cargoInsurance", "Nakliyat Sigortası"],
  ["vehicleInsurance", "Araç Trafik Sigortası"],
];
const REQUIREMENT_OPTIONS = [["inactive", "Pasif"], ["optional", "İsteğe bağlı"], ["required", "Zorunlu"]];

const STATUS_OPTIONS = [
  { value: "all", label: "Tümü" },
  { value: "pending", label: "Onay Bekliyor" },
  { value: "approved", label: "Onaylandı" },
  { value: "rejected", label: "Reddedildi" },
];

const CATEGORY_OPTIONS = [
  { value: "all", label: "Tümü" },
  { value: "company", label: "Firma Belgeleri" },
  { value: "vehicle", label: "Araç Belgeleri" },
];

const STATUS_LABELS = {
  pending: "Onay Bekliyor",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  missing: "Eksik",
};

const getSupportedVehicles = (vehicles = []) =>
  vehicles.filter(
    (vehicle) =>
      vehicle?.active !== false &&
      SUPPORTED_VEHICLE_TYPES.includes(parseInt(vehicle?.type, 10)),
  );

const formatDate = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("tr-TR");
  } catch {
    return "-";
  }
};

const actionIconSx = (color, hoverBg) => ({
  width: 32,
  height: 32,
  color,
  border: "1px solid #E4E9F0",
  borderRadius: "8px",
  "&:hover": { backgroundColor: hoverBg, borderColor: "transparent" },
});

const statusPill = (status) => {
  const styles =
    status === "approved"
      ? { backgroundColor: "#EAF7F0", color: "#116B45", borderColor: "#BBE5D0" }
      : status === "rejected"
      ? { backgroundColor: "#FEECEE", color: "#B42318", borderColor: "#F6CDCF" }
      : status === "pending"
      ? { backgroundColor: "#FFF7E6", color: "#92600A", borderColor: "#F2DCAE" }
      : { backgroundColor: "#EEF2F6", color: "#6B7280", borderColor: "#DDE3EA" };

  return (
    <div
      style={{
        backgroundColor: styles.backgroundColor,
        color: styles.color,
        border: `1px solid ${styles.borderColor}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 6,
        padding: "1px 7px",
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
    >
      {STATUS_LABELS[status] || status || "-"}
    </div>
  );
};

export default class CompanyDocumentApprovals extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      documents: [],
      pendingCount: 0,
      category: "all",
      status: "all",
      editDialogOpen: false,
      editLoading: false,
      editDocument: null,
      editDriverVehicles: [],
      editAllowedTypes: [],
      replaceFile: null,
      replaceLoading: false,
      rejectDialogOpen: false,
      rejectReason: "",
      rejectDocument: null,
      requirements: null,
      requirementsSaving: false,
    };
  }

  componentDidMount() {
    this.loadDocuments();
    if (API.hasPermission(0)) this.loadRequirements();
  }

  loadRequirements = () => {
    API.fetch("getcompanydocumentrequirements", {}).then((response) => {
      if (response?.matrix) this.setState({ requirements: response.matrix });
    });
  };

  saveRequirements = () => {
    this.setState({ requirementsSaving: true });
    API.fetch("savecompanydocumentrequirements", { matrix: this.state.requirements })
      .then((response) => {
        if (!response?.saved) global.showPopup("Hata", response?.error || "Belge kuralları kaydedilemedi.");
        else this.loadDocuments();
      })
      .catch(() => global.showPopup("Hata", "Belge kuralları kaydedilemedi."))
      .finally(() => this.setState({ requirementsSaving: false }));
  };

  loadDocuments = () => {
    this.setState({ loading: true });
    API.fetch("getcompanydocumentapprovals", {
      category: this.state.category,
      status: this.state.status,
    }).then((response) => {
      if (response?.error) {
        global.showPopup("Hata", response.error);
        this.setState({ loading: false });
        return;
      }

      this.setState({
        loading: false,
        documents: response.documents || [],
        pendingCount: response.pendingCount || 0,
      });
    });
  };

  handleView = (row) => {
    API.fetch("getcompanydocumentviewurl", {
      driverId: row.driverId,
      id: row.id,
    }).then((response) => {
      if (response?.error || !response?.url) {
        global.showPopup("Hata", response?.error || "Belge açılmadı.");
        return;
      }

      window.open(response.url, "_blank");
    });
  };

  handleApprove = (row) => {
    API.fetch("savecompanydocument", {
      driverId: row.driverId,
      id: row.id,
      status: "approved",
    }).then((response) => {
      if (response?.error) {
        global.showPopup("Hata", response.error);
        return;
      }

      this.loadDocuments();
    });
  };

  openRejectDialog = (row) => {
    this.setState({
      rejectDialogOpen: true,
      rejectReason: row.rejectionReason || "",
      rejectDocument: row,
    });
  };

  submitReject = () => {
    if (!this.state.rejectReason.trim()) {
      global.showPopup("Hata", "Ret nedeni zorunludur.");
      return;
    }

    API.fetch("savecompanydocument", {
      driverId: this.state.rejectDocument.driverId,
      id: this.state.rejectDocument.id,
      status: "rejected",
      rejectionReason: this.state.rejectReason.trim(),
    }).then((response) => {
      if (response?.error) {
        global.showPopup("Hata", response.error);
        return;
      }

      this.setState({
        rejectDialogOpen: false,
        rejectReason: "",
        rejectDocument: null,
      });
      this.loadDocuments();
    });
  };

  openEditDialog = (row) => {
    this.setState({ editLoading: true, editDialogOpen: true, replaceFile: null });
    API.fetch("getdriver", { _id: row.driverId }).then((response) => {
      if (response?.error) {
        global.showPopup("Hata", "Sürücü detayı alınamadı.");
        this.setState({ editDialogOpen: false, editLoading: false });
        return;
      }

      const currentDocument = (response.companyDocuments || []).find(
        (item) => item._id === row.id,
      );

      if (!currentDocument) {
        global.showPopup("Hata", "Belge bulunamadı.");
        this.setState({ editDialogOpen: false, editLoading: false });
        return;
      }

      this.setState({
        editLoading: false,
        editDriverVehicles: getSupportedVehicles(response.vehicles || []).filter((vehicle) =>
          (response.companyDocuments || []).some((document) => document.documentType === "vehicleInsurance" && String(document.vehicleId) === String(vehicle._id))),
        editAllowedTypes: [...new Set((response.companyDocuments || []).map((document) => document.documentType))],
        editDocument: {
          id: currentDocument._id,
          driverId: row.driverId,
          documentType: currentDocument.documentType,
          vehicleId: currentDocument.vehicleId || "",
          status: currentDocument.status || "pending",
          rejectionReason: currentDocument.rejectionReason || "",
          expiryDate: currentDocument.expiryDate
            ? new Date(currentDocument.expiryDate).toISOString().slice(0, 10)
            : "",
        },
      });
    });
  };

  submitEdit = () => {
    const { editDocument } = this.state;
    if (!editDocument) return;

    if (
      editDocument.status === "rejected" &&
      !`${editDocument.rejectionReason || ""}`.trim()
    ) {
      global.showPopup("Hata", "Ret nedeni zorunludur.");
      return;
    }

    API.fetch("savecompanydocument", {
      driverId: editDocument.driverId,
      id: editDocument.id,
      documentType: editDocument.documentType,
      vehicleId:
        editDocument.documentType === "vehicleInsurance"
          ? editDocument.vehicleId
          : undefined,
      status: editDocument.status,
      rejectionReason: editDocument.rejectionReason,
      expiryDate: editDocument.expiryDate || undefined,
    }).then((response) => {
      if (response?.error) {
        global.showPopup("Hata", response.error);
        return;
      }

      this.setState({
        editDialogOpen: false,
        editDocument: null,
        editDriverVehicles: [],
        replaceFile: null,
      });
      this.loadDocuments();
    });
  };

  submitReplace = () => {
    if (!this.state.replaceFile || !this.state.editDocument) {
      global.showPopup("Hata", "Dosya seçilmedi.");
      return;
    }

    this.setState({ replaceLoading: true });
    const formData = new FormData();
    formData.append("driverId", this.state.editDocument.driverId);
    formData.append("id", this.state.editDocument.id);
    formData.append("file", this.state.replaceFile);

    API.sendFile("replacecompanydocument", formData).then((response) => {
      this.setState({ replaceLoading: false });
      if (response?.error) {
        global.showPopup("Hata", response.error);
        return;
      }

      this.setState({
        editDialogOpen: false,
        editDocument: null,
        editDriverVehicles: [],
        replaceFile: null,
      });
      this.loadDocuments();
    });
  };

  handleDelete = (row) => {
    global.openPrompt(
      "Belgeyi silmek istediğinize emin misiniz?",
      "Bu belge silinirse sürücünün yeniden yüklemesi gerekir.",
      "Sil",
      () => {
        API.fetch("deletecompanydocument", {
          driverId: row.driverId,
          id: row.id,
        }).then((response) => {
          if (response?.error) {
            global.showPopup("Hata", response.error);
            return;
          }

          this.loadDocuments();
        });
      },
    );
  };

  render() {
    const editDocumentTypeOptions = DOCUMENT_COLUMNS
      .filter(([value]) => this.state.editAllowedTypes.includes(value))
      .map(([value, label]) => ({ value, label }));
    const columns = [
      {
        field: "driverName",
        headerName: "Sürücü",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => (
          <a href={`../driver/${params.row.driverId}`}>{params.value}</a>
        ),
      },
      { field: "companyName", headerName: "Firma", minWidth: 220, flex: 1.2 },
      { field: "documentTitle", headerName: "Belge Türü", minWidth: 220, flex: 1.1 },
      { field: "vehiclePlate", headerName: "Plaka", minWidth: 140, flex: 0.8 },
      {
        field: "uploadDate",
        headerName: "Yükleme Tarihi",
        minWidth: 170,
        flex: 0.9,
        valueFormatter: ({ value }) => formatDate(value),
      },
      {
        field: "status",
        headerName: "Durum",
        minWidth: 120,
        flex: 0.6,
        renderCell: (params) => statusPill(params.value),
      },
      {
        field: "actions",
        headerName: "İşlemler",
        minWidth: 220,
        flex: 0.9,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            <Tooltip title="Görüntüle">
              <IconButton
                size="small"
                sx={actionIconSx("#475467", "#EEF2F7")}
                onClick={() => this.handleView(params.row)}
              >
                <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {params.row.status !== "approved" && (
              <Tooltip title="Onayla">
                <IconButton
                  size="small"
                  sx={actionIconSx("#116B45", "#EAF7F0")}
                  onClick={() => this.handleApprove(params.row)}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            {params.row.status !== "rejected" && (
              <Tooltip title="Reddet">
                <IconButton
                  size="small"
                  sx={actionIconSx("#92600A", "#FFF7E6")}
                  onClick={() => this.openRejectDialog(params.row)}
                >
                  <BlockOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Düzenle">
              <IconButton
                size="small"
                sx={actionIconSx("#475467", "#EEF2F7")}
                onClick={() => this.openEditDialog(params.row)}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Sil">
              <IconButton
                size="small"
                sx={actionIconSx("#B42318", "#FEECEE")}
                onClick={() => this.handleDelete(params.row)}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ];

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
          <div>
            <Typography variant="h5" fontWeight={700}>
              Belge Onayları
            </Typography>
            <Typography color={"#6B7280"} style={{ marginTop: 6 }}>
              Onay bekleyen belge sayısı: {this.state.pendingCount}
            </Typography>
          </div>

          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Select
              size="small"
              value={this.state.category}
              onChange={(event) =>
                this.setState({ category: event.target.value }, this.loadDocuments)
              }
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            <Select
              size="small"
              value={this.state.status}
              onChange={(event) =>
                this.setState({ status: event.target.value }, this.loadDocuments)
              }
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        {API.hasPermission(0) && this.state.requirements ? (
          <div style={{ backgroundColor: "white", borderRadius: 10, padding: 20, marginBottom: 20, overflowX: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div><Typography variant="h6">Araç Tipine Göre Belge Kuralları</Typography><Typography variant="body2" color="text.secondary">Pasif belgeler gizlenir. İsteğe bağlı belgeler doğrulamayı engellemez.</Typography></div>
              <Button variant="contained" disabled={this.state.requirementsSaving} onClick={this.saveRequirements}>Kuralları Kaydet</Button>
            </div>
            <table style={{ width: "100%", minWidth: 900, borderCollapse: "collapse" }}>
              <thead><tr><th style={{ textAlign: "left", padding: 8 }}>Araç</th>{DOCUMENT_COLUMNS.map(([key, label]) => <th key={key} style={{ textAlign: "left", padding: 8 }}>{label}</th>)}</tr></thead>
              <tbody>{SUPPORTED_VEHICLE_TYPES.map((type) => <tr key={type} style={{ borderTop: "1px solid #E6E8F0" }}>
                <td style={{ padding: 8, whiteSpace: "nowrap" }}>{VEHICLE_LABELS[type]}</td>
                {DOCUMENT_COLUMNS.map(([documentType]) => <td key={documentType} style={{ padding: 8 }}>
                  <Select size="small" fullWidth value={this.state.requirements[type]?.[documentType] || "inactive"} onChange={(event) => this.setState((state) => ({ requirements: { ...state.requirements, [type]: { ...state.requirements[type], [documentType]: event.target.value } } }))}>
                    {REQUIREMENT_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                  </Select>
                </td>)}
              </tr>)}</tbody>
            </table>
          </div>
        ) : null}

        <div style={{ height: 720, backgroundColor: "white", borderRadius: 10 }}>
          {this.state.loading ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <CircularProgress />
            </div>
          ) : (
            <DataGrid
              rows={this.state.documents}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              rowHeight={60}
              columnHeaderHeight={64}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F8FAFC",
                },
                "& .MuiDataGrid-cell": {
                  borderColor: "#EEF2F7",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                },
              }}
            />
          )}
        </div>

        <Dialog
          open={this.state.rejectDialogOpen}
          onClose={() => this.setState({ rejectDialogOpen: false, rejectReason: "", rejectDocument: null })}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Belgeyi reddet</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              multiline
              minRows={4}
              fullWidth
              margin="dense"
              label="Ret nedeni"
              value={this.state.rejectReason}
              onChange={(event) => this.setState({ rejectReason: event.target.value })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ rejectDialogOpen: false, rejectReason: "", rejectDocument: null })}>
              İptal
            </Button>
            <Button color="warning" variant="contained" onClick={this.submitReject}>
              Kaydet
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={this.state.editDialogOpen}
          onClose={() => this.setState({ editDialogOpen: false, editDocument: null, editDriverVehicles: [], replaceFile: null })}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Belgeyi düzenle</DialogTitle>
          <DialogContent>
            {this.state.editLoading || !this.state.editDocument ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                <CircularProgress />
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 8 }}>
                <Select
                  value={this.state.editDocument.documentType}
                  onChange={(event) =>
                    this.setState({
                      editDocument: {
                        ...this.state.editDocument,
                        documentType: event.target.value,
                        vehicleId:
                          event.target.value === "vehicleInsurance"
                            ? this.state.editDriverVehicles[0]?._id || ""
                            : "",
                      },
                    })
                  }
                >
                  {editDocumentTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>

                {this.state.editDocument.documentType === "vehicleInsurance" && (
                  <Select
                    value={this.state.editDocument.vehicleId}
                    onChange={(event) =>
                      this.setState({
                        editDocument: {
                          ...this.state.editDocument,
                          vehicleId: event.target.value,
                        },
                      })
                    }
                  >
                    {this.state.editDriverVehicles.map((vehicle) => (
                      <MenuItem key={vehicle._id} value={vehicle._id}>
                        {vehicle.plate}
                      </MenuItem>
                    ))}
                  </Select>
                )}

                <Select
                  value={this.state.editDocument.status}
                  onChange={(event) =>
                    this.setState({
                      editDocument: {
                        ...this.state.editDocument,
                        status: event.target.value,
                      },
                    })
                  }
                >
                  {STATUS_OPTIONS.filter((item) => item.value !== "all").map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  type="date"
                  label="Geçerlilik tarihi"
                  InputLabelProps={{ shrink: true }}
                  value={this.state.editDocument.expiryDate}
                  onChange={(event) =>
                    this.setState({
                      editDocument: {
                        ...this.state.editDocument,
                        expiryDate: event.target.value,
                      },
                    })
                  }
                />

                <TextField
                  multiline
                  minRows={3}
                  label="Ret nedeni"
                  value={this.state.editDocument.rejectionReason}
                  onChange={(event) =>
                    this.setState({
                      editDocument: {
                        ...this.state.editDocument,
                        rejectionReason: event.target.value,
                      },
                    })
                  }
                />

                <div>
                  <Button component="label" variant="outlined">
                    Dosya Değiştir
                    <input
                      hidden
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={(event) =>
                        this.setState({ replaceFile: event.target.files?.[0] || null })
                      }
                    />
                  </Button>
                  {this.state.replaceFile && (
                    <Typography style={{ marginTop: 8 }}>
                      {this.state.replaceFile.name}
                    </Typography>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ editDialogOpen: false, editDocument: null, editDriverVehicles: [], replaceFile: null })}>
              Kapat
            </Button>
            <Button variant="outlined" onClick={this.submitReplace} disabled={!this.state.replaceFile || this.state.replaceLoading}>
              {this.state.replaceLoading ? "Yükleniyor..." : "Dosyayı Güncelle"}
            </Button>
            <Button variant="contained" onClick={this.submitEdit} disabled={this.state.editLoading}>
              Kaydet
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
